import { BottomSheet } from '@/commons/BottomSheet';
import React, { useState } from 'react'

export const PtCostCalculator = () => { 
    
    const [ showResult, setShowResult ] = useState(false);
    const [ result, setResult ] = useState(null);

    const [ formValues, setFormValues ] = useState({
        gender : "Male",
        age : 30,
        height : 170,
        weight : 60,
        type: "Freelancer",
        disease: "No",
        goal: "Lose Weight",
        frequency: "2",
        location: "Condo Gym"
    })

    
    const handleChange = (e) => {   
        const { name, value } = e.target;
        
        // When trainer type changes to Gym, auto-set location to Commercial Gym
        if (name === 'type' && value === 'Gym') {
            setFormValues({...formValues, [name]: value, location: 'Commercial Gym'})
        } else {
            setFormValues({...formValues, [name]: value})
        }
        setShowResult(false);
    }

    const calculateCost = (e) => {
        e.preventDefault()
        let bmi = formValues.weight/formValues.height/formValues.height*10000
        let baseCost = 90 // Base freelance rate
        let adjustments = [];
        
        // BMI adjustments
        if(bmi > 30){
            if(formValues.age > 40){
                baseCost += 35
                adjustments.push({ factor: 'High BMI + Age 40+', adjustment: '+$35', reason: 'Requires specialized health monitoring' });
            } else {
                baseCost += 20
                adjustments.push({ factor: 'High BMI', adjustment: '+$20', reason: 'Weight management focus needed' });
            }
        } else if(bmi > 25){
            if(formValues.age > 40){
                baseCost += 15
                adjustments.push({ factor: 'Overweight + Age 40+', adjustment: '+$15', reason: 'Careful progression required' });
            } else {
                baseCost += 10
                adjustments.push({ factor: 'Slightly overweight', adjustment: '+$10', reason: 'Body recomposition focus' });
            }
        } else if(bmi < 18.5){
            if(formValues.age > 40){
                baseCost += 25
                adjustments.push({ factor: 'Underweight + Age 40+', adjustment: '+$25', reason: 'Careful muscle building approach' });
            } else {
                baseCost += 10
                adjustments.push({ factor: 'Underweight', adjustment: '+$10', reason: 'Mass gaining program needed' });
            }
        }
        
        // Trainer type adjustment
        if(formValues.type === "Gym"){
            baseCost += 40
            adjustments.push({ factor: 'Commercial Gym Trainer', adjustment: '+$40', reason: 'Gym overhead costs included' });
        }
        
        // Health condition adjustment
        if(formValues.disease === "Yes"){
            baseCost += 30
            adjustments.push({ factor: 'Medical condition', adjustment: '+$30', reason: 'Specialized training & monitoring required' });
        }
        
        // Goal-based adjustment
        if(formValues.goal === "Get Stage Ready Looks"){
            baseCost += 40
            adjustments.push({ factor: 'Competition prep', adjustment: '+$40', reason: 'Advanced programming + nutrition coaching' });
        } else if(formValues.goal === "Build Muscle"){
            baseCost += 15
            adjustments.push({ factor: 'Muscle building', adjustment: '+$15', reason: 'Hypertrophy specialization' });
        }

        // Location adjustment
        if(formValues.location === "Home"){
            baseCost += 20
            adjustments.push({ factor: 'Home training', adjustment: '+$20', reason: 'Travel time & convenience fee' });
        } else if(formValues.location === "Outdoor"){
            baseCost -= 10
            adjustments.push({ factor: 'Outdoor training', adjustment: '-$10', reason: 'No facility costs' });
        }

        // Calculate package costs
        const sessionsPerWeek = parseInt(formValues.frequency);
        const monthlySessionsCount = sessionsPerWeek * 4;
        const monthlyTotal = baseCost * monthlySessionsCount;
        const packageDiscount = sessionsPerWeek >= 3 ? 0.15 : sessionsPerWeek >= 2 ? 0.10 : 0;
        const discountedMonthly = monthlyTotal * (1 - packageDiscount);

        // BMI category
        let bmiCategory = '';
        if (bmi < 18.5) bmiCategory = 'Underweight';
        else if (bmi < 25) bmiCategory = 'Normal';
        else if (bmi < 30) bmiCategory = 'Overweight';
        else bmiCategory = 'Obese';

        setResult({
            bmi: bmi.toFixed(1),
            bmiCategory,
            perSession: baseCost,
            sessionsPerWeek,
            monthlySessionsCount,
            monthlyTotal,
            packageDiscount: packageDiscount * 100,
            discountedMonthly: Math.round(discountedMonthly),
            yearlyEstimate: Math.round(discountedMonthly * 12),
            adjustments,
            trainerType: formValues.type,
            recommendation: formValues.type === "Gym" 
                ? "💡 Consider a freelance trainer to save ~$40/session while getting the same quality training at your condo gym or ActiveSG!"
                : "✅ Great choice! Freelance trainers offer better value without gym membership fees."
        });
        setShowResult(true);
    }

  return (    
    <>
    <section className='max-w-5xl mx-auto py-12 px-2'>
        <header className='mb-6'>
            <h2 className='text-xl md:text-2xl font-extrabold text-center text-white'>🧮 Personal Trainer Cost Calculator Singapore</h2>
            <p className='text-base text-center text-white/70 py-5'>
                Estimate what you should expect to pay for personal training in Singapore based on your profile, fitness goal and whether you train with a
                freelance trainer or a commercial gym trainer.
            </p>
        </header>
        <div className='max-w-4xl mx-auto relative overflow-hidden bg-white/4 backdrop-blur-xl border border-orange-400/20 text-white space-y-5 px-4 py-8 rounded-2xl shadow-xl shadow-orange-500/10'>
            <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-orange-400/30 to-transparent" />
            <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-orange-500/8 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 rounded-full bg-amber-500/6 blur-3xl pointer-events-none" />
            <div className='flex justify-center'>
                <form className='text-sm w-full' onSubmit={(e) => calculateCost(e)}>
                    <div className='relative z-10 grid md:grid-cols-3 gap-6'>
                        {/* Personal Info */}
                        <div className='space-y-4'>
                            <h3 className='font-bold text-lg border-b border-white/10 pb-2 text-orange-400'>👤 Your Profile</h3>
                            <div className='grid grid-cols-2 gap-3'>
                                <div>
                                    <label className="block text-xs uppercase px-1 mb-1">Gender</label>
                                    <select className="w-full px-3 h-10 text-white bg-white/10 border border-white/10 rounded-xl focus:outline-none focus:ring-2 ring-orange-400/50"
                                        name="gender" value={formValues.gender} onChange={(e) => handleChange(e)}>
                                        <option>Male</option>
                                        <option>Female</option>
                                        <option>Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs uppercase px-1 mb-1">Age</label>
                                    <input type="number" min="15" max="80"
                                        className="w-full px-3 py-2 text-white bg-white/10 border border-white/10 rounded-xl focus:outline-none focus:ring-2 ring-orange-400/50"
                                        placeholder="30" name="age" value={formValues.age} onChange={(e) => handleChange(e)} required 
                                    />
                                </div>
                            </div>
                            <div className='grid grid-cols-2 gap-3'>
                                <div>
                                    <label className="block text-xs uppercase px-1 mb-1">Height (cm)</label>
                                    <input type="number" min="100" max="220"
                                        className="w-full px-3 py-2 text-white bg-white/10 border border-white/10 rounded-xl focus:outline-none focus:ring-2 ring-orange-400/50"
                                        placeholder="170" name="height" value={formValues.height} onChange={(e) => handleChange(e)} required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs uppercase px-1 mb-1">Weight (kg)</label>
                                    <input type="number" min="30" max="200"
                                        className="w-full px-3 py-2 text-white bg-white/10 border border-white/10 rounded-xl focus:outline-none focus:ring-2 ring-orange-400/50"
                                        placeholder="70" name="weight" value={formValues.weight} onChange={(e) => handleChange(e)} required 
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs uppercase px-1 mb-1">Health Conditions?</label>
                                <select className="w-full px-3 h-10 text-white bg-white/10 border border-white/10 rounded-xl focus:outline-none focus:ring-2 ring-orange-400/50"
                                    name="disease" value={formValues.disease} onChange={(e) => handleChange(e)}>
                                    <option value="No">No medical conditions</option>
                                    <option value="Yes">Heart/Lung/Joint issues</option>
                                </select>
                            </div>
                        </div>

                        {/* Training Preferences */}
                        <div className='space-y-4'>
                            <h3 className='font-bold text-lg border-b border-white/10 pb-2 text-orange-400'>🎯 Training Goals</h3>
                            <div>
                                <label className="block text-xs uppercase px-1 mb-1">Fitness Goal</label>
                                <select className="w-full px-3 h-10 text-white bg-white/10 border border-white/10 rounded-xl focus:outline-none focus:ring-2 ring-orange-400/50"
                                    name="goal" value={formValues.goal} onChange={(e) => handleChange(e)}>
                                    <option>Lose Weight</option>
                                    <option>Build Muscle</option>
                                    <option>Stay Fit</option>
                                    <option>Get Stage Ready Looks</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs uppercase px-1 mb-1">Sessions Per Week</label>
                                <select className="w-full px-3 h-10 text-white bg-white/10 border border-white/10 rounded-xl focus:outline-none focus:ring-2 ring-orange-400/50"
                                    name="frequency" value={formValues.frequency} onChange={(e) => handleChange(e)}>
                                    <option value="1">1x per week</option>
                                    <option value="2">2x per week (Recommended)</option>
                                    <option value="3">3x per week</option>
                                    <option value="4">4x per week</option>
                                </select>
                            </div>
                        </div>

                        {/* Trainer Preferences */}
                        <div className='space-y-4'>
                            <h3 className='font-bold text-lg border-b border-white/10 pb-2 text-orange-400'>👨‍🏫 Trainer Type</h3>
                            <div>
                                <label className="block text-xs uppercase px-1 mb-1">Trainer Type</label>
                                <select className="w-full px-3 h-10 text-white bg-white/10 border border-white/10 rounded-xl focus:outline-none focus:ring-2 ring-orange-400/50"
                                    name="type" value={formValues.type} onChange={(e) => handleChange(e)}>
                                    <option value="Freelancer">Freelance Trainer (Best Value)</option>
                                    <option value="Gym">Commercial Gym Trainer</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs uppercase px-1 mb-1">Training Location</label>
                                {formValues.type === 'Gym' ? (
                                    <div className="w-full px-3 h-10 text-white/30 bg-white/5 border border-white/10 rounded-xl flex items-center cursor-not-allowed">
                                        Commercial Gym (Required for Gym PT)
                                    </div>
                                ) : (
                                    <select className="w-full px-3 h-10 text-white bg-white/10 border border-white/10 rounded-xl focus:outline-none focus:ring-2 ring-orange-400/50"
                                        name="location" value={formValues.location} onChange={(e) => handleChange(e)}>
                                        <option value="Condo Gym">Condo Gym</option>
                                        <option value="ActiveSG">ActiveSG Gym</option>
                                        <option value="Home">Home Training</option>
                                        <option value="Outdoor">Outdoor/Park</option>
                                    </select>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className='text-center pt-6'>
                        <button type='submit' className='relative font-bold bg-linear-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 py-3 px-8 rounded-2xl text-lg shadow-lg shadow-orange-500/20 transition-all'>
                            Calculate My PT Cost →
                        </button>
                    </div>
                </form>
            </div>
        </div>

        {/* Results Modal using BottomSheet */}
        <BottomSheet 
            isOpen={showResult} 
            onClose={() => setShowResult(false)}
            maxWidth="lg"
        >
            <BottomSheet.Header>
                <BottomSheet.Title>📊 Your PT Cost Estimate</BottomSheet.Title>
                <BottomSheet.Subtitle>Based on your profile and preferences</BottomSheet.Subtitle>
            </BottomSheet.Header>
            <BottomSheet.Content>
                {result && (
                    <div className='space-y-4'>
                        {/* BMI Info */}
                        <div className='bg-linear-to-br from-green-50 to-emerald-50 p-4 rounded-xl'>
                            <div className='grid grid-cols-2 md:grid-cols-4 gap-4 text-center'>
                                <div>
                                    <p className='text-xs text-gray-500'>Your BMI</p>
                                    <p className='text-xl font-bold text-blue-600'>{result.bmi}</p>
                                    <p className='text-xs text-gray-500'>{result.bmiCategory}</p>
                                </div>
                                <div>
                                    <p className='text-xs text-gray-500'>Per Session</p>
                                    <p className='text-xl font-bold text-orange-600'>${result.perSession}</p>
                                </div>
                                <div>
                                    <p className='text-xs text-gray-500'>Monthly ({result.monthlySessionsCount} sessions)</p>
                                    <p className='text-xl font-bold text-green-600'>${result.discountedMonthly}</p>
                                    {result.packageDiscount > 0 && <p className='text-xs text-green-500'>{result.packageDiscount}% discount</p>}
                                </div>
                                <div>
                                    <p className='text-xs text-gray-500'>Yearly Estimate</p>
                                    <p className='text-xl font-bold text-purple-600'>${result.yearlyEstimate.toLocaleString()}</p>
                                </div>
                            </div>
                        </div>

                        {/* Price Breakdown */}
                        {result.adjustments.length > 0 && (
                            <div className='bg-white p-4 rounded-xl border border-gray-100'>
                                <h4 className='font-semibold text-gray-700 mb-2'>💰 Price Breakdown:</h4>
                                <div className='space-y-1 text-sm'>
                                    <div className='flex justify-between'>
                                        <span>Base rate (Freelance PT)</span>
                                        <span className='font-semibold'>$80</span>
                                    </div>
                                    {result.adjustments.map((adj, i) => (
                                        <div key={i} className='flex justify-between text-gray-600'>
                                            <span>{adj.factor} <span className='text-xs text-gray-400'>({adj.reason})</span></span>
                                            <span className={adj.adjustment.startsWith('+') ? 'text-red-500' : 'text-green-500'}>{adj.adjustment}</span>
                                        </div>
                                    ))}
                                    <div className='flex justify-between border-t pt-2 mt-2 font-bold'>
                                        <span>Your estimated rate</span>
                                        <span className='text-orange-600'>${result.perSession}/session</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Recommendation */}
                        <div className={`p-4 rounded-xl ${result.trainerType === "Gym" ? 'bg-amber-50 border border-amber-200' : 'bg-green-100 border border-green-300'}`}>
                            <p className='text-sm'>{result.recommendation}</p>
                        </div>
                    </div>
                )}
            </BottomSheet.Content>
            <BottomSheet.Footer>
                <div className='flex gap-3'>
                    <BottomSheet.CloseButton variant="secondary">Close</BottomSheet.CloseButton>
                </div>
            </BottomSheet.Footer>
        </BottomSheet>
    </section>
    </>
  )
}
